<template>
  <div class="patients standard-layout">
    <div class="d-flex justify-space-between flex-column flex-md-row">
      <h2 class="mb-8 standard-layout__heading">
        {{ $ucfirst($t("patient.list")) }}
      </h2>
    </div>
    <v-text-field
      v-model="search"
      prepend-inner-icon="mdi-magnify"
      flat
      solo
      filled
      :label="$ucfirst($t('patient.search'))"
      no-filter
      @input="onSearch"
      class="patients__search-patients"
    ></v-text-field>
    <div class="patients__table">
      <!-- Data Table -->
      <v-data-table
        class="simple-table"
        fixed-header
        :headers="headers"
        :items="patients"
        :server-items-length="totalItems"
        :options.sync="options"
        :loading="loading"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 30, 40, 50],
          itemsPerPageText: $ucfirst($t(`pagination.itemsPerPage`)),
          pageText: $ucfirst($t(`pagination.pageText`)),
        }"
      >
        <template v-slot:no-data>
          <div class="pa-4">
            {{ $t('noRecordsMatchingYourSearchTerm') }}
          </div>
        </template>

        <template v-slot:[`item.enrollmentDate`]="{ item }">
          <div class="d-flex justify-space-between align-center">
            {{ item.enrollmentDate }}
            <div class="d-flex align-center">
              <v-btn
                class="ml-4"
                fab
                text
                small
                right
                :to="{
                  name: 'PatientOverview',
                  params: { patientId: item.patientId.toString() },
                }"
              >
                <v-icon dark>mdi-file-search</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </v-data-table>
    </div>
    <div class="patients__controls-bottom">
      <div class="patients__page-indicator">
        {{
          $ucfirst(
            $t("pagination.showingpage", {
              params: { current: currentPage, total: totalPages },
            })
          )
        }}
      </div>
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        total-visible="7"
        light
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import { debounce } from "debounce";
import moment from "moment";
import { getAllPatients as getAllPatientsQuery } from "../../store/patient-list/queries";

export default {
  name: "PatientList",
  data() {
    return {
      headers: [
        { text: this.$ucfirst(this.$t("pdf.medicationPrescription.subjectId", {catalog: 'messages'})), value: "subjectId" },
        { text: this.$ucfirst(this.$t("patient.gender")), value: "gender" },
        {
          text: this.$ucfirst(this.$t("patient.yearofbirth")),
          value: "birthDate",
        },
        {
          text: this.$ucfirst(this.$t("patient.startdate")),
          value: "enrollmentDate",
        },
      ],
      patients: [],
      totalItems: 0,
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["subjectId"],
        sortDesc: [true],
      },
      search: "",
    };
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },
  computed: {
    totalPages() {
      if (!this.options?.itemsPerPage) {
        return 1;
      }

      return Math.ceil(this.totalItems / this.options.itemsPerPage) || 1;
    },
    currentPage: {
      get() {
        return this.options.page;
      },
      set(page) {
        this.options.page = page;
      },
    },
  },
  methods: {
    // Called when the search input changes
    onSearch: debounce(function () {
      // Reset to the first page when searching
      this.options.page = 1;
      this.fetchData();
    }, 1000),
    async fetchData() {
      try {
        this.loading = true;

        const orders = [];

        if (this.options.sortBy.length) {
          orders.push({
            field: this.options.sortBy[0],
            direction: this.options.sortDesc[0] ? "desc" : "asc",
          });
        }

        console.log(this.options);

        const { data, errors } = await this.$app.apolloClient.query({
          query: getAllPatientsQuery,
          variables: {
            searchString: this.search,
            limit: this.options.itemsPerPage,
            offset: (this.options.page - 1) * this.options.itemsPerPage,
            orders,
          },
        });

        if (data && !errors) {
          this.totalItems = data.patientUsers.count;
          this.patients = data.patientUsers.edges.map((edge) => {
            const node = edge.node;

            return {
              subjectId: node.subjectId,
              gender: this.$t(node.patient.gender),
              birthDate: node.patient.birthdate.slice(0, 4),
              enrollmentDate: moment(node.enrollmentDate).format("DD-MM-YYYY"),
              patientId: node.patient.patientId,
            };
          });
        } else if (errors.length) {
          errors.forEach((error) => {
            this.$store.dispatch("error/submit", error, { root: true });
          });
          this.patients = [];
        }
      } catch (error) {
        this.$store.dispatch("error/submit", error, { root: true });
        this.patients = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Initial fetch when component mounts.
    this.fetchData();
  },
};
</script>

<style lang="scss">
.patients {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.patients__search-patients {
  flex-grow: 0;
  margin-bottom: 24px !important;
}

.patients__table {
  flex-grow: 1;
  align-items: flex-start;
}

.patients__controls-top,
.patients__controls-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.patients__page-indicator {
  display: flex;
  align-items: center;
}
</style>
